import { accountsApiRtkQuery } from '@chillibean/accounts-api-client-sdk';
import { colors, sizing, useToast } from '@chillibean/component-library';
import { FormsModule, Account, FormContainer, FormContainerType, User, TrialContainer } from '@chillibean/forms';
import { trialContainersApiRtkQuery } from '@chillibean/trial-containers-api-client-sdk';
import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { getBaseAPIUrl } from '../../../../api/apiUtils';
import { trialContainerFetchRequest } from '../../../../api/trial_container/trialContainerApi';
import { AssetRenderer } from '../../../assets/AssetRenderer';
import { useAccountData, useGetAccountQuery } from '../../redux/accountSlice';
import { selectInternalResult } from '../../redux/internal.slice';
import { setUserActivity } from '../../redux/userActivity.slice';
import { useActivityCheckEvent } from '../hook';
import AssetFormsIconButton from '../presentational/AssetFormsIconButton';
import AssetInformation from '../presentational/AssetInformation';
import AssetTitle from '../presentational/AssetTitle';
import ActivityLogger from './ActivityLogger';

const AssetRendererMemoized = memo(AssetRenderer);

/**
 * Requirements:
 * - Asset preview should fill entire column which height match the height of the screen.
 * - Asset info should display below asset preview.
 * - Asset column should scroll inside its container.
 */
function AssetForms() {
  // VARIABLES
  const assetOffset = 130;

  // HOOKS
  // React
  const [trialContainer, setTrialContainer] = useState();
  const [trialContainerError, setTrialContainerError] = useState();
  const [currentUser, setCurrentUser] = useState();
  // Store
  const dispatch = useDispatch();
  const { accountId, assetId, authenticityToken, libraryId, user } = useSelector(selectInternalResult);
  const { account } = useAccountData({ accountId });
  // RTK Query
  const { useGetChapterDefinitionsQuery } = accountsApiRtkQuery;
  const { useGetAssetQuery, useGetAssetChaptersQuery, useUpdateAssetChaptersMutation } = trialContainersApiRtkQuery;
  useGetAccountQuery({ id: accountId }, { skip: !accountId });
  const { data: chapterDefinitions } = useGetChapterDefinitionsQuery({ accountId }, { skip: !accountId });
  const {
    data: asset,
    error: assetError,
    isLoading: isAssetLoading,
  } = useGetAssetQuery(
    {
      accountId: accountId,
      assetId: assetId,
      trialContainerId: libraryId,
    },
    { skip: !accountId || !assetId || !libraryId },
  );
  const { data: chapters, isLoading: isChaptersLoading } = useGetAssetChaptersQuery(
    { accountId: accountId, assetId: assetId, trialContainerId: libraryId },
    { skip: !accountId || !assetId || !libraryId },
  );
  const [updateAssetChapters] = useUpdateAssetChaptersMutation();
  // Router
  const { path } = useRouteMatch();
  // Custom
  useActivityCheckEvent({
    handleUserActivity: setUserActivity,
  });
  const { showErrorToast, showSuccessToast } = useToast();

  // METHODS
  const handleNavigateAway = () => {
    window.location.href = `/libraries/${libraryId}/assets/${assetId}`;
  };

  const onChaptersSubmit = useCallback(
    async (chaptersData) => {
      try {
        const response = await updateAssetChapters({
          accountId: accountId,
          assetId: assetId,
          trialContainerId: libraryId,
          body: { authenticityToken: authenticityToken, chapters: chaptersData },
        }).unwrap();

        showSuccessToast('Chapters Updated');

        return Promise.resolve(response);
      } catch (error) {
        if (error.data && error.data.errors.message) {
          showErrorToast(error.data.errors);
        }

        return Promise.reject(error.data.errors.fields);
      }
    },
    [accountId, assetId, libraryId, authenticityToken],
  );

  const onVideoPlay = useCallback(() => {
    dispatch(setUserActivity('watching video'));
  }, []);

  // EFFECTS
  useEffect(() => {
    if (!accountId || !libraryId) return;

    const fetchTrialContainer = async () => {
      try {
        const response = await trialContainerFetchRequest(accountId, libraryId);
        setTrialContainer(new TrialContainer(response.data));
        setCurrentUser(
          new User(user)
            .setAccountPermissions({
              admin: user.accountAdmin,
              manager: user.accountManager,
            })
            .setTrialContainerPermissions(response.data.permissions),
        );
      } catch (error) {
        setTrialContainerError(error);
      }
    };

    fetchTrialContainer();
  }, [accountId, libraryId, user]);

  return (
    <>
      <ActivityLogger />
      <Wrapper>
        <Sidebar>
          <AssetFormsIconButton navigateAway={handleNavigateAway} />
        </Sidebar>
        <PageContent>
          <AssetTitle requestAction={() => trialContainerFetchRequest(accountId, `${libraryId}/assets/${assetId}`)} />
          <Container>
            <ElementWrapper $assetOffset={assetOffset}>
              <div>
                <AssetRendererWrapper $assetOffset={assetOffset}>
                  <AssetRendererMemoized
                    account={account}
                    chapterDefinitions={chapterDefinitions}
                    chapters={chapters}
                    data={asset}
                    error={assetError || trialContainerError}
                    loading={isAssetLoading || isChaptersLoading}
                    onVideoPlay={onVideoPlay}
                    onChaptersSubmit={onChaptersSubmit}
                    user={user}
                    zoomOnWheel={false}
                  />
                </AssetRendererWrapper>
                {asset && <AssetInformation infoFields={asset?.assetInfos} />}
              </div>
            </ElementWrapper>
            <ElementWrapper $assetOffset={assetOffset}>
              {Boolean(authenticityToken && trialContainer && currentUser) && (
                <FormsContainer>
                  <FormsModule
                    account={
                      new Account({
                        id: accountId,
                      })
                    }
                    authenticityToken={authenticityToken}
                    baseApiUrl={getBaseAPIUrl()}
                    basePath={path}
                    currentUser={currentUser}
                    formContainer={
                      new FormContainer({
                        id: Number(assetId),
                        name: 'Asset',
                        type: FormContainerType.ASSET,
                      })
                    }
                    trialContainer={trialContainer}
                  />
                </FormsContainer>
              )}
            </ElementWrapper>
          </Container>
        </PageContent>
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  display: flex;
`;

const Sidebar = styled.div`
  height: 100vh;
  padding-top: 30px;

  position: sticky;
  top: 0;
  left: 0;
  flex: 0 0 80px;
  text-align: center;

  background: linear-gradient(270deg, #930000 0%, rgba(153, 0, 0, 0.902655) 50.65%);
`;

const PageContent = styled.div`
  padding: 0px 60px;
  margin: 30px auto;
  width: 100%;
`;
const Container = styled.div`
  display: flex;
  flex-wrap: wrap;

  gap: 16px;
  width: 100%;
  max-width: 1600px;
  padding: 30px 60px 0 60px;
  margin: auto;
`;

const ElementWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 475px;
  height: ${(props) => (props.$assetOffset ? `calc(100vh - ${props.$assetOffset}px)` : 'auto')};

  flex-grow: 1;
  position: relative;
  overflow: auto;

  @media (min-width: 1200px) {
    width: calc(50% - 16px);
  }
`;

const AssetRendererWrapper = styled.div`
  display: flex;
  height: calc(100vh - ${(props) => props.$assetOffset}px);
`;

const FormsContainer = styled.div`
  padding: 0;
  height: 100%;
  border: 1px solid ${colors.black.medium};
  border-radius: ${sizing.xsmall};

  // ! override default CP styles
  .error {
    margin-top: 0;
  }
`;

export default AssetForms;
